import { ProcessingFeeBasis } from "@redotech/crdb-enums/fulfillment/processing-fee-basis";
import { Currency } from "@redotech/money/currencies";
import * as z from "zod";

export const PROCESSING_CURRENCIES: readonly Currency[] = [
  Currency.USD,
  Currency.GBP,
  Currency.EUR,
  Currency.CAD,
  Currency.AUD,
];

export interface ReturnsCenterPricing {
  readonly currency?: Currency | null;
  readonly fee: number | null;
  readonly basis: ProcessingFeeBasis | null;
  readonly externalFee?: number | null;
  readonly externalBasis?: ProcessingFeeBasis | null;
}

export interface ProcessingBillingConfig {
  readonly global: {
    readonly currency?: Currency;
    readonly fee: number | null;
    readonly basis: ProcessingFeeBasis;
    readonly externalFee?: number | null;
    readonly externalBasis?: ProcessingFeeBasis | null;
  };
  readonly centers: Readonly<Record<string, ReturnsCenterPricing>>;
}

export interface ProcessingBillingChange {
  readonly path: string;
  readonly prev: unknown;
  readonly next: unknown;
}

export interface ExternalProcessingPricing {
  readonly fee: number | null;
  readonly basis: ProcessingFeeBasis | null;
}

export const returnsCenterPricingSchema = z
  .object({
    fee: z.number().nullable(),
    basis: z.enum(ProcessingFeeBasis).nullable(),
    currency: z
      .enum(Currency)
      .refine((currency) => PROCESSING_CURRENCIES.includes(currency), {
        message: "Unsupported processing currency",
      })
      .nullable()
      .optional(),
    externalFee: z.number().nullable().optional(),
    externalBasis: z.enum(ProcessingFeeBasis).nullable().optional(),
  })
  .refine(
    (pricing) =>
      pricing.fee == null || pricing.fee === 0 || pricing.basis != null,
    { message: "A returns-center fee override requires a fee basis" },
  )
  .refine(
    (pricing) =>
      (pricing.externalFee == null) === (pricing.externalBasis == null),
    { message: "An external returns fee requires a fee basis" },
  )
  .refine((pricing) => pricing.externalFee == null || pricing.externalFee > 0, {
    message: "An external returns fee must be greater than 0",
  })
  .refine(
    (pricing) =>
      pricing.currency == null ||
      (pricing.fee != null && pricing.basis != null),
    {
      message: "A center currency override requires an explicit fee and basis",
    },
  );

export const processingBillingConfigSchema = z.object({
  global: z
    .object({
      currency: z
        .enum(Currency)
        .refine((currency) => PROCESSING_CURRENCIES.includes(currency), {
          message: "Unsupported processing currency",
        })
        .optional(),
      fee: z.number().nullable(),
      basis: z.enum(ProcessingFeeBasis),
      externalFee: z.number().nullable().optional(),
      externalBasis: z.enum(ProcessingFeeBasis).nullable().optional(),
    })
    .refine(
      (pricing) =>
        (pricing.externalFee == null) === (pricing.externalBasis == null),
      { message: "An external returns fee requires a fee basis" },
    )
    .refine(
      (pricing) => pricing.externalFee == null || pricing.externalFee > 0,
      { message: "An external returns fee must be greater than 0" },
    ),
  centers: z.record(z.string(), returnsCenterPricingSchema),
});

export const PROCESSING_CURRENCY_PATH = "redoManagedReturns.processingCurrency";

export const PROCESSING_COST_PATH = "redoManagedReturns.processingCost";

export const PROCESSING_FEE_PATH = "redoManagedReturns.processingFee";

export const PROCESSING_FEE_BASIS_PATH =
  "redoManagedReturns.processingFeeBasis";

export const RETURNS_CENTER_PRICING_PATH =
  "redoManagedReturns.returnsCenterPricing";

export const EXTERNAL_PROCESSING_PRICING_PATH =
  "redoManagedReturns.externalProcessingPricing";

export const EXTERNAL_PROCESSING_BILLING_ENABLED = true;

export const DEFAULT_PROCESSING_BILLING_CONFIG: ProcessingBillingConfig = {
  global: {
    currency: Currency.USD,
    fee: null,
    basis: ProcessingFeeBasis.PerReturn,
    externalFee: null,
    externalBasis: null,
  },
  centers: {},
};

export function returnsCenterPricingPath(returnsCenterId: string): string {
  return `${RETURNS_CENTER_PRICING_PATH}.${returnsCenterId}`;
}

export function parseReturnsCenterPricingPath(path: string): string | null {
  const prefix = `${RETURNS_CENTER_PRICING_PATH}.`;
  return path.startsWith(prefix) ? path.slice(prefix.length) : null;
}

export function isProcessingFeeBasisPath(path: string): boolean {
  return path === PROCESSING_FEE_BASIS_PATH;
}

export function isProcessingBillingPath(path: string): boolean {
  return (
    path === PROCESSING_CURRENCY_PATH ||
    path === PROCESSING_COST_PATH ||
    path === PROCESSING_FEE_PATH ||
    path === PROCESSING_FEE_BASIS_PATH ||
    path === EXTERNAL_PROCESSING_PRICING_PATH ||
    parseReturnsCenterPricingPath(path) !== null
  );
}

export function parseProcessingFeeBasisValue(
  value: unknown,
): ProcessingFeeBasis | null {
  return value == null ? null : z.enum(ProcessingFeeBasis).parse(value);
}

export function parseProcessingFeeValue(value: unknown): number | null {
  return value == null ? null : z.number().parse(value);
}

export function parseExternalProcessingPricingValue(
  value: unknown,
): ExternalProcessingPricing {
  return z
    .object({
      fee: z.number().nullable(),
      basis: z.enum(ProcessingFeeBasis).nullable(),
    })
    .refine((pricing) => (pricing.fee == null) === (pricing.basis == null))
    .refine((pricing) => pricing.fee == null || pricing.fee > 0)
    .parse(value);
}

export function parseReturnsCenterPricingValue(
  value: unknown,
): ReturnsCenterPricing | null {
  return value == null
    ? null
    : normalizeReturnsCenterPricing(returnsCenterPricingSchema.parse(value));
}

export function normalizeReturnsCenterPricing(
  pricing: ReturnsCenterPricing | null | undefined,
): ReturnsCenterPricing | null {
  return pricing == null ||
    (pricing.fee === 0 &&
      pricing.currency == null &&
      pricing.externalFee == null)
    ? null
    : pricing;
}

export interface StoredProcessingBillingRow {
  readonly returnsCenterId: string | null;
  readonly currency?: Currency | null;
  readonly fee: number | null;
  readonly basis: ProcessingFeeBasis | null;
  readonly externalFee?: number | null;
  readonly externalBasis?: ProcessingFeeBasis | null;
}

export function readProcessingBillingConfig(
  rows: readonly StoredProcessingBillingRow[],
): ProcessingBillingConfig {
  const global = rows.find((row) => row.returnsCenterId === null);
  const centers: Record<string, ReturnsCenterPricing> = {};
  for (const row of rows) {
    const pricing = centerPricingOrNull({
      fee: row.fee,
      basis: row.basis,
      ...(row.externalFee == null ? {} : { externalFee: row.externalFee }),
      ...(row.externalBasis == null
        ? {}
        : { externalBasis: row.externalBasis }),
      ...(row.currency == null ? {} : { currency: row.currency }),
    });
    if (row.returnsCenterId != null && pricing != null) {
      centers[row.returnsCenterId] = pricing;
    }
  }
  return {
    global: {
      currency: global?.currency ?? Currency.USD,
      fee: global?.fee ?? null,
      basis: global?.basis ?? DEFAULT_PROCESSING_BILLING_CONFIG.global.basis,
      ...(global?.externalFee == null
        ? {}
        : { externalFee: global.externalFee }),
      ...(global?.externalBasis == null
        ? {}
        : { externalBasis: global.externalBasis }),
    },
    centers,
  };
}

function centerPricingOrNull(
  pricing: ReturnsCenterPricing | undefined,
): ReturnsCenterPricing | null {
  const override = normalizeReturnsCenterPricing(pricing);
  return override == null ||
    (override.fee == null &&
      override.basis == null &&
      override.currency == null &&
      override.externalFee == null)
    ? null
    : override;
}

function samePricing(
  a: ReturnsCenterPricing | null,
  b: ReturnsCenterPricing | null,
): boolean {
  return (
    a?.fee === b?.fee &&
    a?.basis === b?.basis &&
    (a?.currency ?? null) === (b?.currency ?? null) &&
    (a?.externalFee ?? null) === (b?.externalFee ?? null) &&
    (a?.externalBasis ?? null) === (b?.externalBasis ?? null)
  );
}

export const PROCESSING_CURRENCY_REQUIRES_FEE_MESSAGE =
  "Enter a processing fee (0 is allowed) before changing the processing currency";

/**
 * Denominating the global fee with no fee would make the stored currency win
 * over the legacy USD fee and bill 0, so a currency change, or a save that
 * first denominates a fee-less legacy config, must carry an explicit fee.
 */
export function assertGlobalFeeForDenominatedPricing(params: {
  readonly prevCurrency: Currency;
  readonly prevFee: number | null;
  readonly prevBasis: ProcessingFeeBasis;
  readonly next: ProcessingBillingConfig["global"];
}): void {
  const { prevCurrency, prevFee, prevBasis, next } = params;
  if (next.fee != null || next.currency === undefined) {
    return;
  }
  if (
    next.currency !== prevCurrency ||
    (prevFee == null && next.basis !== prevBasis)
  ) {
    throw new Error(PROCESSING_CURRENCY_REQUIRES_FEE_MESSAGE);
  }
}

export function diffProcessingBillingConfig(
  prev: ProcessingBillingConfig,
  submitted: ProcessingBillingConfig,
): ProcessingBillingChange[] {
  const next = preservePricingFieldsOmittedByOlderClients(prev, submitted);
  const changes: ProcessingBillingChange[] = [];
  if (
    next.global.currency !== undefined &&
    (prev.global.currency ?? Currency.USD) !== next.global.currency
  ) {
    assertGlobalFeeForDenominatedPricing({
      prevCurrency: prev.global.currency ?? Currency.USD,
      prevFee: prev.global.fee,
      prevBasis: prev.global.basis,
      next: next.global,
    });
    return [{ path: PROCESSING_CURRENCY_PATH, prev, next }];
  }
  if (
    (prev.global.externalFee ?? null) !== (next.global.externalFee ?? null) ||
    (prev.global.externalBasis ?? null) !== (next.global.externalBasis ?? null)
  ) {
    changes.push({
      path: EXTERNAL_PROCESSING_PRICING_PATH,
      prev: {
        fee: prev.global.externalFee ?? null,
        basis: prev.global.externalBasis ?? null,
      },
      next: {
        fee: next.global.externalFee ?? null,
        basis: next.global.externalBasis ?? null,
      },
    });
  }
  if (prev.global.fee !== next.global.fee) {
    changes.push({
      path: PROCESSING_COST_PATH,
      prev: prev.global.fee,
      next: next.global.fee,
    });
  }
  if (prev.global.basis !== next.global.basis) {
    changes.push({
      path: PROCESSING_FEE_BASIS_PATH,
      prev: prev.global.basis,
      next: next.global.basis,
    });
  }
  const centerIds = new Set([
    ...Object.keys(prev.centers),
    ...Object.keys(next.centers),
  ]);
  for (const returnsCenterId of [...centerIds].sort()) {
    const before = centerPricingOrNull(prev.centers[returnsCenterId]);
    const after = centerPricingOrNull(next.centers[returnsCenterId]);
    if (!samePricing(before, after)) {
      changes.push({
        path: returnsCenterPricingPath(returnsCenterId),
        prev: before,
        next: after,
      });
    }
  }
  const currency = next.global.currency ?? prev.global.currency ?? Currency.USD;
  if (
    changes.length > 0 &&
    (currency !== Currency.USD ||
      Object.values(next.centers).some(
        (pricing) => pricing.currency !== undefined,
      ) ||
      Object.values(prev.centers).some((pricing) => pricing.currency != null))
  ) {
    if (next.global.currency === undefined) {
      throw new Error(
        "Processing pricing requires its currency; reload the settings page",
      );
    }
    assertGlobalFeeForDenominatedPricing({
      prevCurrency: prev.global.currency ?? Currency.USD,
      prevFee: prev.global.fee,
      prevBasis: prev.global.basis,
      next: next.global,
    });
    return [
      {
        path: PROCESSING_CURRENCY_PATH,
        prev,
        next: { ...next, global: { ...next.global, currency } },
      },
    ];
  }
  return changes;
}

/** Older payloads must not silently clear or re-denominate a pinned center price. */
export function preservePricingFieldsOmittedByOlderClients(
  previous: ProcessingBillingConfig,
  submitted: ProcessingBillingConfig,
): ProcessingBillingConfig {
  const centers = { ...submitted.centers };
  for (const [id, before] of Object.entries(previous.centers)) {
    if (before.currency == null) {
      continue;
    }
    const after = centers[id];
    if (after?.currency !== undefined) {
      continue;
    }
    if (!after || after.fee !== before.fee || after.basis !== before.basis) {
      throw new Error(
        "Center processing pricing requires its currency; reload the settings page",
      );
    }
    centers[id] = { ...after, currency: before.currency };
  }
  for (const [id, before] of Object.entries(previous.centers)) {
    const after = centers[id];
    if (!after) {
      continue;
    }
    centers[id] = {
      ...after,
      ...(after.externalFee === undefined
        ? { externalFee: before.externalFee }
        : {}),
      ...(after.externalBasis === undefined
        ? { externalBasis: before.externalBasis }
        : {}),
    };
  }
  return {
    ...submitted,
    global: {
      ...submitted.global,
      ...(submitted.global.externalFee === undefined
        ? { externalFee: previous.global.externalFee }
        : {}),
      ...(submitted.global.externalBasis === undefined
        ? { externalBasis: previous.global.externalBasis }
        : {}),
    },
    centers,
  };
}
